<template>
  <div
    ref="root"
    class="pfe"
    role="dialog"
    aria-modal="true"
    aria-labelledby="pfe-title"
    tabindex="-1"
    data-test="post-op-editor-panel"
  >
    <!-- HEADER: title, the master switch (classic only), close, and the tabs -->
    <header class="pfe-head">
      <div class="pfe-head-row">
        <div class="pfe-icon" aria-hidden="true"><i class="fa" :class="formsSettings ? 'fa-clipboard' : 'fa-link'"></i></div>
        <div class="pfe-head-text">
          <h1 id="pfe-title" class="pfe-title">{{ headTitle }}</h1>
          <p class="pfe-sub" :data-test="formsSettings ? 'pfe-subtitle' : null">{{ headSubtitle }}</p>
        </div>
        <!-- Forms Settings (formsSettings=true): no switch in the header — every sending tab opens with its own SendSwitchCard. -->
        <auto-send-switch
          v-if="ready && !formsSettings"
          :state="switchView"
          :busy="switchBusy || saving"
          :error="switchError"
          @toggle="onSwitchToggle"
        />
        <button type="button" class="pfe-close" aria-label="Close" data-test="pfe-close" @click="requestClose">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>
      <div class="pfe-tabs" :class="{ 'pfe-tabs--scroll': formsSettings }" role="tablist" aria-label="Editor sections">
        <template v-if="!formsSettings">
          <button
            type="button"
            role="tab"
            class="pfe-tab"
            :class="{ 'pfe-tab--on': tab === 'map' }"
            :aria-selected="tab === 'map' ? 'true' : 'false'"
            data-test="pfe-tab-map"
            @click="tab = 'map'"
          >Procedure mappings</button>
          <button
            type="button"
            role="tab"
            class="pfe-tab"
            :class="{ 'pfe-tab--on': tab === 'message' }"
            :aria-selected="tab === 'message' ? 'true' : 'false'"
            data-test="pfe-tab-message"
            @click="tab = 'message'"
          >Post-op message<span v-if="ready && validation.count" class="pfe-tab-badge" data-test="pfe-tab-badge">{{ validation.count }}</span></button>
        </template>
        <!-- Forms Settings: New Patients · Medical History Update · Procedure mappings · Post-op message. The tab row scrolls sideways when it
             does not fit (board 4a). The old two keep their pfe-tab-map / pfe-tab-message hooks and the pfe-tab-badge on the post-op tab. -->
        <template v-else>
          <button
            v-for="t in tabDefs"
            :key="t.key"
            type="button"
            role="tab"
            class="pfe-tab"
            :class="{ 'pfe-tab--on': tab === t.key }"
            :aria-selected="tab === t.key ? 'true' : 'false'"
            :data-test="t.test"
            @click="tab = t.key"
          >{{ t.label }}<span v-if="t.badge" class="pfe-tab-badge" :data-test="t.badgeTest">{{ t.badge }}</span></button>
        </template>
      </div>
    </header>

    <!-- BODY: [data-pfe-bounds] is the visible region every popup must stay inside -->
    <div class="pfe-scroll" data-pfe-bounds data-test="pfe-scroll">
      <div v-if="loading" class="pfe-state" data-test="pfe-loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading procedure forms&hellip;
      </div>
      <div v-else-if="loadError" class="pfe-state pfe-state--error" role="alert" data-test="pfe-load-error">
        <div><i class="fa fa-exclamation-triangle" aria-hidden="true"></i> {{ loadError }}</div>
        <div class="pfe-state-actions">
          <button type="button" class="pfe-btn" data-test="pfe-retry" @click="load">Try again</button>
          <button type="button" class="pfe-btn pfe-btn--ghost" @click="$emit('close')">Close</button>
        </div>
      </div>
      <template v-else>
        <!-- CLASSIC (formsSettings=false): today's two tabs, untouched. -->
        <mappings-tab
          v-if="!formsSettings && tab === 'map'"
          :rows="rows"
          :forms="forms"
          :forms-loading="formsLoading"
          :forms-error="formsError"
          :code-options="codeOptions"
          :codes-loading="codesLoading"
          :analysis="analysis"
          :open-codes-key="openCodesKey"
          @change="onRowChange"
          @remove="removeRow"
          @add="addRow"
        />
        <message-tab
          v-else-if="!formsSettings"
          :draft="draft"
          :errors="fieldErrors"
          :site="siteInfo"
          @change="onDraftChange"
        />
        <!-- FORMS SETTINGS (formsSettings=true). The two sending tabs stay MOUNTED (v-show) so a half-edited tab survives a tab switch; they
             register with this host (provide formsSettingsHost; contract in src/utils/formsSettings/host.js). -->
        <template v-else>
          <new-patients-tab
            v-show="tab === 'newPatients'"
            :site-id="siteId"
            :context="newPatientContext"
            :forms="catalogForms"
            :packets="catalogPackets"
            :catalog-failed="catalogFailed"
            :site="siteInfo"
            :readiness="readiness"
            :code-options="codeOptions"
            :codes-loading="codesLoading"
            @toast="flash"
          />
          <med-hx-tab
            v-show="tab === 'medHx'"
            :site-id="siteId"
            :context="medHxContext"
            :forms="catalogForms"
            :packets="catalogPackets"
            :catalog-failed="catalogFailed"
            :site="siteInfo"
            :readiness="readiness"
            @toast="flash"
          />
          <mappings-tab
            v-if="tab === 'map'"
            :rows="rows"
            :forms="forms"
            :forms-loading="formsLoading"
            :forms-error="formsError"
            :code-options="codeOptions"
            :codes-loading="codesLoading"
            :analysis="analysis"
            :open-codes-key="openCodesKey"
            @change="onRowChange"
            @remove="removeRow"
            @add="addRow"
          />
          <template v-else-if="tab === 'message'">
            <!-- the post-op switch card opens its tab (board 3a): same states, same confirm (AutoSendConfirm below), same reasons as the old header switch -->
            <div class="pfe-tabtop" data-test="pfe-post-op-switch-top">
              <send-switch-card
                title="Auto-send post-op instructions"
                :state="switchView"
                :busy="switchBusy || saving"
                :error="switchError"
                @turn-off="onSwitchToggle"
                @request-on="onSwitchToggle"
              />
            </div>
            <message-tab
              :draft="draft"
              :errors="fieldErrors"
              :site="siteInfo"
              @change="onDraftChange"
            />
          </template>
        </template>
      </template>
    </div>

    <!-- SAVE BAR -->
    <footer class="pfe-savebar" data-test="pfe-savebar">
      <span class="pfe-save-icon" :class="'pfe-save-icon--' + bar.kind" aria-hidden="true">
        <i v-if="bar.kind === 'saving'" class="fa fa-circle-o-notch fa-spin"></i>
        <i v-else-if="bar.kind === 'issues' || bar.kind === 'error'" class="fa fa-exclamation-triangle"></i>
        <i v-else-if="bar.kind === 'dirty'" class="fa fa-circle"></i>
        <i v-else class="fa fa-check-circle"></i>
      </span>
      <span class="pfe-save-msg" :class="'pfe-save-msg--' + bar.kind" role="status" data-test="pfe-save-status">{{ bar.message }}</span>
      <span class="pfe-spacer"></span>
      <button type="button" class="pfe-btn pfe-btn--ghost" :disabled="!ready || !bar.canReset" data-test="pfe-reset" @click="resetAll">Reset</button>
      <button type="button" class="pfe-btn pfe-btn--primary" :disabled="!ready || !bar.canSave" data-test="pfe-save" @click="onSave">Save</button>
    </footer>

    <auto-send-confirm
      v-if="confirmOpen"
      :analysis="analysis"
      :when-text="whenText"
      :busy="switchBusy"
      @cancel="confirmOpen = false"
      @confirm="confirmEnable"
    />

    <div v-if="discardOpen" class="pfe-scrim" data-test="pfe-discard-scrim">
      <div class="pfe-dialog" role="alertdialog" aria-modal="true" aria-labelledby="pfe-discard-title" data-test="pfe-discard">
        <div id="pfe-discard-title" class="pfe-dialog-title">Discard unsaved changes?</div>
        <p v-if="!formsSettings" class="pfe-dialog-body">Your edits to the procedure forms and the post-op message haven&rsquo;t been saved.</p>
        <p v-else class="pfe-dialog-body" data-test="pfe-discard-body">Your changes to Forms Settings haven&rsquo;t been saved.</p>
        <div class="pfe-dialog-actions">
          <button type="button" class="pfe-btn" data-test="pfe-discard-keep" @click="discardOpen = false">Keep editing</button>
          <button type="button" class="pfe-btn pfe-btn--danger" data-test="pfe-discard-confirm" @click="$emit('close')">Discard changes</button>
        </div>
      </div>
    </div>

    <div v-if="toast.text" class="pfe-toast" :class="{ 'pfe-toast--error': toast.kind === 'error' }" role="status" data-test="pfe-toast">
      <i :class="toast.kind === 'error' ? 'fa fa-exclamation-circle' : 'fa fa-check-circle'" aria-hidden="true"></i>{{ toast.text }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import AutoSendSwitch from './AutoSendSwitch.vue';
import AutoSendConfirm from './AutoSendConfirm.vue';
import MappingsTab from './MappingsTab.vue';
import MessageTab from './MessageTab.vue';
import SendSwitchCard from './SendSwitchCard.vue';
import NewPatientsTab from './NewPatientsTab.vue';
import MedHxTab from './MedHxTab.vue';
import { fetchPostOpContext, savePostOpSettings } from '@/api/postOp';
import { fetchFormsSettingsContext } from '@/api/formsSettings';
import {
  TAB_LABELS, resolveInitialTab, emptyTabState, anyDirty, anySaving, dirtyInSaveOrder, firstIssueTab,
} from '@/utils/formsSettings/host';
import {
  isOn, cleanRowsForSave, rowsSignature, analyzeRows, buildCodeOptions, chipsForCodes, newRow,
} from '@/utils/postOp/rows';
import {
  draftFromSettings, validateMessageDraft, formatLiveSince, whenSentText,
} from '@/utils/postOp/message';
import { blockedReason, switchState, saveBarState } from '@/utils/postOp/readiness';

const DEFAULT_READINESS = { smsReady: false, emailReady: false, senderEmail: null, signatureEnabled: false, signatureHtml: null };
const TOAST_MS = 2600;

const clone = (v) => JSON.parse(JSON.stringify(v));

/** The part of the draft the server stores (never `enabled` / `liveSince`). */
function messageFields(draft) {
  return {
    delayMinutes: draft.delayMinutes,
    quietStart: draft.quietStart,
    quietEnd: draft.quietEnd,
    sms: { body: draft.sms.body },
    email: { subject: draft.email.subject, body: draft.email.body },
  };
}

function errorText(e, fallback) {
  return (e && e.info && e.info.message) || (e && e.message) || fallback;
}

/**
 * Forms → 🔗: the full-screen "Procedure Forms & Post-op Instructions" editor (plan docs/plans/2026-10-15-post-op-auto-send-plan.md §1, §5b).
 *
 *   Procedure mappings   rows of { label?, codes, consent form, post-op form, Text/Email } — the TX_PRESENTER_SETTINGS_CONSENT blob
 *   Post-op message      delay + quiet hours + ONE shared text + ONE shared email, with a live preview — the POST_OP_SETTINGS blob
 *   Master switch        "Auto-send post-op instructions" (OFF by default); turning it on asks first
 *
 * SAVE = the consent rows through txPresenter/saveSettingsSection {section:'consent'} (only if they changed), THEN the
 * message through forms-service PUT /save-forms-reminder-settings (savePostOpSettings; only if it changed). The switch's confirm saves the rows first so the
 * server's gate (it reads the STORED rows) agrees with the dialog, then sends `enabled:'TRUE'`; `liveSince` is stamped by the
 * server and rendered as returned. Both editors drop rows with the same rule (utils/postOp/rows.js cleanRowsForSave).
 *
 * FORMS SETTINGS (prop `formsSettings`, behind the release gate `formsSettings`; plan docs/plans/2026-10-20-forms-settings-plan.md §2.6):
 * the same host, four tabs — New Patients · Medical History Update · Procedure mappings · Post-op message. `formsSettings=false` (the default) is
 * today's two-tab editor, markup and copy unchanged. With it on:
 *   - the context loads ONCE (fetchFormsSettingsContext) and its newPatient / medHx / forms / packets slices go to the two new tabs as props;
 *   - each sending tab owns a switch card; the post-op one moved from the header to the top of its tab (SendSwitchCard);
 *   - per-tab state { loaded, dirty, errors, saving } (src/utils/formsSettings/host.js). A tab is dirty ONLY after the office edits it, so a post-op
 *     Save never touches New Patients; Save walks the dirty tabs mappings → post-op → New Patients → Medical History and STOPS at the first refusal,
 *     staying open with the server's message; Reset and the discard dialog act on every tab; the bar blames the first dirty tab with problems;
 *   - SAVE NEVER SENDS `enabled` / `activated` (D15a): only a switch card's confirm does, in its own request.
 * The two new tabs register with this host (provide `formsSettingsHost`); the contract is documented in host.js and implemented by WP2b.
 */
export default {
  name: 'ProcedureFormsEditor',
  components: { AutoSendSwitch, AutoSendConfirm, MappingsTab, MessageTab, SendSwitchCard, NewPatientsTab, MedHxTab },
  provide() {
    return { formsSettingsHost: { register: this.registerTab, unregister: this.unregisterTab } };
  },
  props: {
    siteId: { type: String, default: null },
    /** Forms Settings (the four-tab editor). false ⇒ today's two-tab Procedure Forms & Post-op Instructions editor, unchanged. */
    formsSettings: { type: Boolean, default: false },
    /** Which tab opens first (formsSettings only): 'newPatients' | 'medHx' | 'mappings' | 'postOp'. Anything else ⇒ 'newPatients'. */
    initialTab: { type: String, default: '' },
  },
  data() {
    return {
      loading: true,
      loadError: '',
      tab: resolveInitialTab(this.initialTab, this.formsSettings), // internal keys: 'newPatients' | 'medHx' | 'map' | 'message'

      // Forms Settings only: the ONE context response, and the two sending tabs that registered with this host (component instances).
      formsContext: null,
      registered: {},
      saveErrorTab: '',

      rows: [],          // working copy (each row carries a client-only `_key`)
      savedRows: [],     // what the server has
      draft: null,       // working copy of the message fields
      savedDraft: null,
      serverSettings: null, // the full POST_OP_SETTINGS as the server returned it (enabled / liveSince live here)
      readiness: null,
      overlaps: [],

      forms: [],
      formsLoading: false,
      formsError: '',
      codesLoading: false,
      openCodesKey: null,

      saving: false,
      saveError: '',
      serverErrors: {},  // field → message from a 422 INVALID_SETTINGS
      switchBusy: false,
      switchError: '',
      confirmOpen: false,
      discardOpen: false,
      toast: { text: '', kind: 'ok' },
    };
  },
  computed: {
    ready() { return !this.loading && !this.loadError && !!this.draft && !!this.serverSettings; },
    siteID() { return this.siteId; },
    codeOptions() { return buildCodeOptions(this.$store.state.customer && this.$store.state.customer.procCodes); },
    analysis() {
      return analyzeRows(this.rows, { overlaps: this.overlaps, labelForCode: this.labelForCode });
    },
    block() { return blockedReason(this.analysis, this.readiness); },
    enabled() { return isOn(this.serverSettings && this.serverSettings.enabled); },
    timezone() {
      const rows = (this.$store.state.customer && this.$store.state.customer.settings) || [];
      const hit = Array.isArray(rows) ? rows.find((r) => r && r.Property === 'GENERAL_TIMEZONE') : null;
      return (hit && hit.PropertyValue) || '';
    },
    switchView() {
      return switchState({
        enabled: this.enabled,
        liveSinceText: formatLiveSince(this.serverSettings && this.serverSettings.liveSince, this.timezone),
        block: this.block,
      });
    },
    validation() { return this.draft ? validateMessageDraft(this.draft) : { errors: {}, count: 0 }; },
    fieldErrors() { return Object.assign({}, this.serverErrors, this.validation.errors); },
    rowsDirty() { return rowsSignature(this.rows) !== rowsSignature(this.savedRows); },
    messageDirty() { return !!this.draft && JSON.stringify(this.draft) !== JSON.stringify(this.savedDraft); },
    dirty() { return this.formsSettings ? anyDirty(this.tabStates) : (this.rowsDirty || this.messageDirty); },
    bar() {
      if (!this.formsSettings) {
        return saveBarState({ dirty: this.dirty, saving: this.saving, saveError: this.saveError, issueCount: this.validation.count });
      }
      // Forms Settings: the bar blames the FIRST dirty tab with problems ("Fix 2 issues on New Patients to save"); a tab nobody edited never counts.
      const issue = firstIssueTab(this.tabStates);
      return saveBarState({
        dirty: this.dirty,
        saving: this.saving || anySaving(this.tabStates),
        saveError: this.saveError,
        issueCount: issue ? issue.count : 0,
        issueTab: issue ? issue.label : '',
      });
    },

    // ── Forms Settings ──
    headTitle() { return this.formsSettings ? 'Forms Settings' : 'Procedure Forms & Post-op Instructions'; },
    headSubtitle() {
      return this.formsSettings
        ? 'Set up the forms patients fill before and after their visits — new patient paperwork, medical history updates, consent forms and after-care instructions.'
        : 'Match procedure codes to the consent form patients sign before the visit and the after-care instructions we send when the procedure is done.';
    },
    /** { loaded, dirty, errors, saving } per tab. Mappings / Post-op live here; the two sending tabs report through their registration. */
    tabStates() {
      const sending = (key) => {
        const vm = this.registered[key];
        if (!vm) return emptyTabState();
        return { loaded: true, dirty: !!vm.dirty, errors: Number(vm.errorCount) || 0, saving: !!vm.saving };
      };
      return {
        newPatients: sending('newPatients'),
        medHx: sending('medHx'),
        map: { loaded: this.ready, dirty: this.rowsDirty, errors: 0, saving: this.saving },
        message: { loaded: this.ready, dirty: this.messageDirty, errors: this.validation.count, saving: this.saving },
      };
    },
    tabDefs() {
      const states = this.tabStates;
      const sendingBadge = (key) => (states[key].dirty ? states[key].errors : 0); // an untouched tab's prefilled "pick a form" never shows a badge
      return [
        { key: 'newPatients', label: TAB_LABELS.newPatients, test: 'pfe-tab-new-patients', badge: sendingBadge('newPatients'), badgeTest: 'pfe-tab-badge-new-patients' },
        { key: 'medHx', label: TAB_LABELS.medHx, test: 'pfe-tab-medhx', badge: sendingBadge('medHx'), badgeTest: 'pfe-tab-badge-medhx' },
        { key: 'map', label: TAB_LABELS.map, test: 'pfe-tab-map', badge: 0, badgeTest: 'pfe-tab-badge-map' },
        { key: 'message', label: TAB_LABELS.message, test: 'pfe-tab-message', badge: this.ready ? this.validation.count : 0, badgeTest: 'pfe-tab-badge' },
      ];
    },
    newPatientContext() { return (this.formsContext && this.formsContext.newPatient) || null; },
    medHxContext() { return (this.formsContext && this.formsContext.medHx) || null; },
    catalogForms() { return (this.formsContext && Array.isArray(this.formsContext.forms)) ? this.formsContext.forms : []; },
    catalogPackets() { return (this.formsContext && Array.isArray(this.formsContext.packets)) ? this.formsContext.packets : []; },
    catalogFailed() { return !!(this.formsContext && this.formsContext.formsLoadFailed === true); },
    whenText() { return this.draft ? whenSentText(this.draft) : ''; },
    /** The REAL practice, for the preview (the patient stays the fictional Maria Lopez). */
    siteInfo() {
      const customer = this.$store.state.customer || {};
      const site = (Array.isArray(customer.sites) ? customer.sites : []).find((s) => s && s.SiteID === this.siteId) || {};
      const r = this.readiness || DEFAULT_READINESS;
      return {
        name: (customer.user && customer.user.SiteName) || site.Name || '',
        phone: site.OfficePhone || '',
        senderEmail: r.senderEmail || '',
        signatureEnabled: !!r.signatureEnabled,
        signatureHtml: r.signatureHtml || '',
      };
    },
  },
  watch: {
    // "+ Add row" opens the code menu on the new row ONCE — coming back to this tab must not pop it open again.
    tab() { this.openCodesKey = null; },
  },
  created() {
    this.keySeq = 0;
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (this.$refs.root && typeof this.$refs.root.focus === 'function') this.$refs.root.focus();
    this.load();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = this.prevBodyOverflow || '';
    clearTimeout(this.toastTimer);
  },
  methods: {
    // ── loading ─────────────────────────────────────────────────────────────────────────────────────────────────
    nextKey() { this.keySeq += 1; return this.keySeq; },
    withKeys(list) {
      return (Array.isArray(list) ? list : []).map((r) => Object.assign(clone(r), { _key: this.nextKey() }));
    },
    labelForCode(value) {
      const chip = chipsForCodes([value], this.codeOptions)[0];
      return chip ? chip.code : value;
    },
    auth() {
      return this.$store.state.getAuth ? this.$store.state.getAuth() : null;
    },
    async load() {
      if (!this.siteID) {
        this.loading = false;
        this.loadError = 'No site selected.';
        return;
      }
      this.loading = true;
      this.loadError = '';
      try {
        // rows: always the fresh blob, not the 60-second cache — the office may have just run the onboarding seeder
        await this.$store.dispatch('txPresenter/loadSettings', { siteID: this.siteID, force: true });
        const resolved = this.$store.getters['txPresenter/settingsConsentResolved'] || { mappings: [] };
        // Forms Settings loads the WHOLE context (post-op + New Patients + Medical History + forms + packets) once; the classic editor's call is unchanged.
        const ctx = this.formsSettings
          ? await fetchFormsSettingsContext({ auth: this.auth(), siteID: this.siteID })
          : await fetchPostOpContext({ auth: this.auth(), siteID: this.siteID });
        this.formsContext = this.formsSettings ? ctx : null;
        this.rows = this.withKeys(resolved.mappings);
        this.savedRows = clone(this.rows);
        this.serverSettings = ctx.settings;
        this.draft = draftFromSettings(ctx.settings);
        this.savedDraft = clone(this.draft);
        this.readiness = Object.assign({}, DEFAULT_READINESS, ctx.readiness);
        this.overlaps = Array.isArray(ctx.workflowOverlaps) ? ctx.workflowOverlaps : [];
        this.serverErrors = {};
        this.saveError = '';
      } catch (e) {
        this.loadError = errorText(e, 'Couldn’t load the procedure forms. Please try again.');
      } finally {
        this.loading = false;
      }
      if (!this.loadError) {
        this.loadForms();
        this.ensureProcCodes();
      }
    },
    async loadForms() {
      this.formsLoading = true;
      this.formsError = '';
      try {
        const response = await axios.post('/get-forms-for-consent-mapping', { siteID: this.siteID }, { crossdomain: true });
        if (response.data && response.data.success) {
          this.forms = (response.data.data && response.data.data.forms) || [];
        } else {
          this.formsError = (response.data && response.data.message) || 'Unknown error';
        }
      } catch (e) {
        this.formsError = e.message || 'Network error';
      } finally {
        this.formsLoading = false;
      }
    },
    /** customer.procCodes is NOT loaded on app init (see SettingsConsent.vue) — load it here; a no-op when cached. */
    async ensureProcCodes() {
      const cached = (this.$store.state.customer && this.$store.state.customer.procCodes) || [];
      if (Array.isArray(cached) && cached.length > 0) return;
      this.codesLoading = true;
      try {
        await this.$store.dispatch('customer/getProcCodes', { siteID: this.siteID });
      } catch (e) {
        console.warn('[ProcedureFormsEditor] failed to load procedure codes:', e && e.message);
      } finally {
        this.codesLoading = false;
      }
    },

    // ── editing ─────────────────────────────────────────────────────────────────────────────────────────────────
    addRow() {
      const row = Object.assign(newRow(), { _key: this.nextKey() });
      this.rows = this.rows.concat([row]);
      this.openCodesKey = row._key; // "+ Add row" opens the code menu on the new row
    },
    removeRow(index) {
      this.rows = this.rows.filter((_, i) => i !== index);
    },
    onRowChange(index, row) {
      this.saveError = '';
      this.$set(this.rows, index, Object.assign({}, row, { _key: this.rows[index]._key }));
    },
    onDraftChange(next) {
      this.saveError = '';
      this.serverErrors = {};
      this.draft = next;
    },
    resetAll() {
      this.rows = clone(this.savedRows);
      this.draft = clone(this.savedDraft);
      this.serverErrors = {};
      this.saveError = '';
      this.saveErrorTab = '';
      this.switchError = '';
      // Forms Settings: Reset acts on EVERY tab, including the two that registered with this host
      if (this.formsSettings) {
        Object.keys(this.registered).forEach((key) => {
          const vm = this.registered[key];
          if (vm && typeof vm.reset === 'function') vm.reset();
        });
      }
    },

    // ── saving ──────────────────────────────────────────────────────────────────────────────────────────────────
    /**
     * Save what changed. `enable` also sends enabled:'TRUE' (the post-op switch's confirm). Returns true on success.
     * The consent rows go FIRST: the server refuses to enable against the rows it has STORED.
     * (The Forms Settings Save button is saveAll() below; this stays what the post-op switch's confirm calls, in both modes.)
     */
    async save({ enable = false } = {}) {
      if (this.saving || !this.ready) return false;
      if (this.validation.count > 0) return false;
      this.saving = true;
      this.saveError = '';
      this.switchError = '';
      try {
        if (this.rowsDirty) await this.persistRows();
        if (this.messageDirty || enable) await this.persistMessage({ enable });
        this.flash(enable ? 'Auto-send is on' : 'All changes saved');
        return true;
      } catch (e) {
        this.saveError = this.describeSaveFailure(e);
        return false;
      } finally {
        this.saving = false;
      }
    },
    /** The consent rows -> txPresenter/saveSettingsSection {section:'consent'}. Throws on failure. */
    async persistRows() {
      const cleaned = cleanRowsForSave(this.rows);
      await this.$store.dispatch('txPresenter/saveSettingsSection', { section: 'consent', value: { mappings: cleaned } });
      this.rows = this.withKeys(cleaned);
      this.savedRows = clone(this.rows);
    },
    /**
     * The post-op message (and, from the switch's confirm ONLY, `enabled:'TRUE'`) -> PUT /save-forms-reminder-settings. Throws on failure.
     * ⛔ `enable` is true only when the post-op switch's confirm calls save({ enable: true }); the Save button never passes it.
     */
    async persistMessage({ enable = false } = {}) {
      const value = this.messageDirty ? messageFields(this.draft) : {};
      if (enable) value.enabled = 'TRUE';
      const res = await savePostOpSettings({ auth: this.auth(), siteID: this.siteID, value });
      this.serverSettings = res.settings;
      this.draft = draftFromSettings(res.settings);
      this.savedDraft = clone(this.draft);
    },
    /** A failed post-op / rows save -> the sentence for the save bar; also routes the server's field problems / switch reason to where they show. */
    describeSaveFailure(e) {
      const info = e && e.info;
      if (info && info.kind === 'blocked') this.switchError = info.message;
      if (info && info.kind === 'invalid') {
        const map = {};
        // the editor shows the two quiet-hours fields as ONE problem under the window
        (info.errors || []).forEach((f) => { if (f.field) map[f.field === 'quietStart' || f.field === 'quietEnd' ? 'window' : f.field] = f.message; });
        this.serverErrors = map;
        this.tab = 'message';
      }
      return errorText(e, 'Couldn’t save — please try again.');
    },
    onSave() {
      return this.formsSettings ? this.saveAll() : this.save();
    },

    // ── Forms Settings: the sending tabs register here (contract: src/utils/formsSettings/host.js) ─────────────────────
    registerTab(key, vm) {
      this.$set(this.registered, key, vm);
    },
    unregisterTab(key, vm) {
      if (this.registered[key] === vm) this.$delete(this.registered, key);
    },
    /**
     * The Forms Settings Save: walks the DIRTY tabs in order (mappings → post-op → New Patients → Medical History), STOPS at the first refusal and
     * stays open on that tab with the server's sentence in the save bar. A tab nobody edited is never written (D15a), and no tab ever carries
     * `enabled` here. Returns true when every dirty tab saved.
     */
    async saveAll() {
      if (this.saving || !this.ready) return false;
      const states = this.tabStates;
      const blamed = firstIssueTab(states);
      if (blamed) { // Save is disabled in this state; defensive for a keyboard / programmatic call
        this.tab = blamed.key;
        const vm = this.registered[blamed.key];
        if (vm && typeof vm.validate === 'function') vm.validate();
        return false;
      }
      const steps = dirtyInSaveOrder(states);
      if (steps.length === 0) return true;
      this.saving = true;
      this.saveError = '';
      this.saveErrorTab = '';
      this.switchError = '';
      try {
        for (let i = 0; i < steps.length; i += 1) {
          const key = steps[i];
          // eslint-disable-next-line no-await-in-loop
          const res = await this.saveTab(key);
          if (!res.ok) {
            this.saveError = res.message;
            this.saveErrorTab = key;
            this.tab = key;
            return false;
          }
        }
        this.flash('All changes saved');
        return true;
      } finally {
        this.saving = false;
      }
    },
    /** One tab's write -> { ok:true } | { ok:false, message }. Never throws. */
    async saveTab(key) {
      try {
        if (key === 'map') { await this.persistRows(); return { ok: true }; }
        if (key === 'message') { await this.persistMessage({}); return { ok: true }; }
        const vm = this.registered[key];
        if (!vm || typeof vm.save !== 'function') return { ok: true };
        const res = await vm.save();
        if (res && res.ok === false) return { ok: false, message: res.message || 'Couldn’t save — please try again.' };
        return { ok: true };
      } catch (e) {
        return { ok: false, message: key === 'map' || key === 'message' ? this.describeSaveFailure(e) : errorText(e, 'Couldn’t save — please try again.') };
      }
    },

    // ── the master switch ───────────────────────────────────────────────────────────────────────────────────────
    async onSwitchToggle() {
      this.switchError = '';
      if (this.enabled) return this.disableAutoSend();
      if (this.block) return undefined; // locked: the card says why
      if (this.validation.count > 0) {
        this.tab = 'message';
        this.flash('Fix the issues on Post-op message first.', 'error');
        return undefined;
      }
      this.confirmOpen = true;
      return undefined;
    },
    async confirmEnable() {
      this.confirmOpen = false;
      this.switchBusy = true;
      try {
        await this.save({ enable: true });
      } finally {
        this.switchBusy = false;
      }
    },
    /** Switching OFF always works (only `enabled` is sent — any unsaved edits stay unsaved). */
    async disableAutoSend() {
      this.switchBusy = true;
      try {
        const res = await savePostOpSettings({ auth: this.auth(), siteID: this.siteID, value: { enabled: 'FALSE' } });
        this.serverSettings = res.settings;
        this.flash('Auto-send is off');
      } catch (e) {
        this.switchError = errorText(e, 'Couldn’t turn auto-send off. Please try again.');
      } finally {
        this.switchBusy = false;
      }
    },

    // ── chrome ──────────────────────────────────────────────────────────────────────────────────────────────────
    flash(text, kind = 'ok') {
      clearTimeout(this.toastTimer);
      this.toast = { text, kind };
      this.toastTimer = setTimeout(() => { this.toast = { text: '', kind: 'ok' }; }, TOAST_MS);
    },
    requestClose() {
      if (this.saving) return;
      if (this.dirty) this.discardOpen = true;
      else this.$emit('close');
    },
    onKeydown(e) {
      if (e.key !== 'Escape') return;
      // a code / form menu answers Escape itself and stops it before it gets here
      if (this.confirmOpen) { this.confirmOpen = false; return; }
      if (this.discardOpen) { this.discardOpen = false; return; }
      this.requestClose();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.pfe {
  position: fixed;
  inset: 0;
  z-index: $pfe-z-editor;
  display: flex;
  flex-direction: column;
  background: #f7faf9;
  color: $pp-text;
  font-family: inherit;
  outline: none;
  animation: pfeIn 0.2s ease;
}
@keyframes pfeIn { from { opacity: 0; } to { opacity: 1; } }

.pfe-head {
  flex: none;
  background: $pp-surface;
  border-bottom: 1px solid $pp-border;
  padding: 22px 28px 0 32px;
}
.pfe-head-row { display: flex; align-items: flex-start; gap: 18px; flex-wrap: wrap; }
.pfe-icon {
  width: 52px;
  height: 52px;
  flex: none;
  border-radius: 14px;
  background: $pp-brand-subtle;
  color: $pp-brand;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}
.pfe-head-text { flex: 1 1 340px; min-width: 0; padding-top: 2px; }
.pfe-title {
  margin: 0;
  font-family: $pp-font-display;
  font-weight: 800;
  font-size: 26px;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: $pp-text;
}
.pfe-sub { margin: 6px 0 0; max-width: 620px; font-size: 14.5px; line-height: 1.5; color: $pfe-neutral-500; }
.pfe-close {
  flex: none;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: $pfe-neutral-500;
  font-size: 16px;
  cursor: pointer;

  &:hover { background: $pfe-neutral-100; color: $pp-text; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 2px; }
}

.pfe-tabs { display: flex; gap: 4px; margin-top: 16px; }
.pfe-tab {
  position: relative;
  height: 42px;
  padding: 0 18px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  font: inherit;
  font-size: 14.5px;
  font-weight: 700;
  color: $pfe-neutral-500;
  cursor: pointer;

  &:hover { color: $pp-text; }
  &--on { color: $pp-brand; border-bottom-color: $pp-brand; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: -2px; }
}
// Forms Settings has four tabs: the row scrolls sideways instead of wrapping or squeezing (board 4a).
.pfe-tabs--scroll {
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;

  .pfe-tab { flex: none; white-space: nowrap; }
}
// The post-op switch card at the top of its tab (Forms Settings), lined up with the tab's own 32px gutters.
.pfe-tabtop { max-width: 1320px; margin: 0 auto; padding: 22px 32px 0; }
.pfe-tab-badge {
  margin-left: 8px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: $pp-red;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  line-height: 18px;
  display: inline-block;
  text-align: center;
}

.pfe-scroll { flex: 1; min-height: 0; overflow-y: auto; }
.pfe-state {
  max-width: 560px;
  margin: 80px auto;
  padding: 0 24px;
  text-align: center;
  font-size: 14.5px;
  color: $pfe-neutral-500;

  &--error { color: $pp-red-text; }
  i { margin-right: 6px; }
}
.pfe-state-actions { margin-top: 16px; display: flex; justify-content: center; gap: 10px; }

.pfe-savebar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 32px;
  background: $pp-surface;
  border-top: 1px solid $pp-border;
  box-shadow: 0 -6px 20px rgba(12, 15, 14, 0.04);
}
.pfe-spacer { flex: 1; }
.pfe-save-icon {
  display: inline-flex;
  font-size: 15px;
  color: $pp-brand;

  &--dirty { color: $pp-amber; font-size: 10px; }
  &--issues, &--error { color: $pfe-red-ink; }
  &--saving { color: $pfe-neutral-500; }
  &--clean { color: $pp-brand; }
}
.pfe-save-msg {
  font-size: 14px;
  font-weight: 500;
  color: $pfe-neutral-600;

  &--dirty { font-weight: 700; color: $pp-text; }
  &--issues, &--error { font-weight: 700; color: $pfe-red-ink; }
}

.pfe-btn {
  @include pfe-button(secondary);

  &--ghost { @include pfe-button(ghost); }
  &--primary { @include pfe-button(primary); }
  &--danger {
    @include pfe-button(secondary);
    color: $pp-red-text;
    &:not(:disabled):hover { border-color: $pp-red; background: $pp-red-subtle; }
  }
}

.pfe-scrim {
  position: absolute;
  inset: 0;
  z-index: 50;
  background: rgba(12, 15, 14, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.pfe-dialog {
  width: 440px;
  max-width: 100%;
  padding: 24px 26px 20px;
  background: $pp-surface;
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(12, 15, 14, 0.28);
}
.pfe-dialog-title { font-family: $pp-font-display; font-weight: 800; font-size: 20px; letter-spacing: -0.02em; }
.pfe-dialog-body { margin: 10px 0 0; font-size: 14.5px; line-height: 1.5; color: $pfe-neutral-600; }
.pfe-dialog-actions { margin-top: 20px; display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }

.pfe-toast {
  position: absolute;
  left: 50%;
  bottom: 84px;
  transform: translateX(-50%);
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: calc(100% - 32px);
  padding: 12px 16px;
  border-radius: 12px;
  background: #0c0f0e;
  color: #fff;
  font-size: 14px;
  box-shadow: 0 12px 32px rgba(12, 15, 14, 0.28);

  i { color: #7fd9bd; }
  &--error i { color: #ff9b9e; }
}

@media (max-width: 900px) {
  .pfe-tabtop { padding: 16px 16px 0; }
  .pfe-head { padding: 16px 16px 0; }
  .pfe-title { font-size: 21px; }
  .pfe-savebar { padding: 12px 16px; }
}
</style>
